import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, Search, Maximize2, Hand } from "lucide-react";
import { Translator } from "./translator";

const BASE = "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com";
const WS_URL = "wss://wy3b83ksu7.execute-api.eu-north-1.amazonaws.com/production";

const deviceId = (() => {
  const k = "teche_device_id";
  let id = localStorage.getItem(k);
  if (!id) {
    id = (crypto.randomUUID?.() || (Date.now() + "-" + Math.random()));
    localStorage.setItem(k, id);
  }
  return id;
})();

export default function ChatFloat() {
  const { pathname } = useLocation();





  // Hide ChatFloat on full messages routes
  const isMessageRoute = useMemo(
    () => /^\/(messages|dm)(\/|$)/.test(pathname),
    [pathname]
  );


  const showFloatUI = !isMessageRoute;


  const { translateText, lang } = Translator();

  const baseTextByLang = useMemo(
    () => ({
      it: {
        textNoMore: "— Non ci sono altri messaggi —",
        textHE: "Il tuo amico",
        texttyoing: "sta scrivendo…",
        loginHint: "Accedi, trova amici e inizia a chattare",
        loginBubbleAria: "Accedi per usare i messaggi",
        ariaOpenMessages: "Apri i messaggi",
        ariaCloseMessages: "Chiudi i messaggi",

        chatsTitle: "TecheChat",
        seeAllTitle: "Vedi tutto",
        searchPlaceholder: "Cerca nelle chat",
        searchingLabel: "Ricerca in corso…",
        noResults: "Nessun risultato",

        panelLoginMessage: "Accedi per usare i messaggi.",
        loadMore: "Carica altri",
        noChats: "Nessuna chat",
        findFriendsCta: "Trova amici nella Community",
        seeAllFooter: "Vedi tutto in Messenger",

        headdmPlaceholder: "Scrivi un messaggio…",
        headdmLoadPrev: "Carica i messaggi precedenti",
        headdmNoMsgs: "Ancora nessun messaggio.",
        headdmCloseAria: "Chiudi chat",

        chatrowSayHi: "Di' ciao",
        chatrowSayHiLabel: "Di' ciao",
      },

      en: {
        textNoMore: "— No more conversations —",
        textHE: "Your friend",
        texttyoing: "is typing…",
        loginHint: "Log in, find friends and start chatting",
        loginBubbleAria: "Log in to use messages",
        ariaOpenMessages: "Open messages",
        ariaCloseMessages: "Close messages",

        chatsTitle: "TecheChat",
        seeAllTitle: "See all",
        searchPlaceholder: "Search chats",
        searchingLabel: "Searching…",
        noResults: "No results",

        panelLoginMessage: "Log in to use messages.",
        loadMore: "Load more",
        noChats: "No chats yet",
        findFriendsCta: "Find friends in Community",
        seeAllFooter: "See all in Messenger",

        headdmPlaceholder: "Write a message…",
        headdmLoadPrev: "Load previous messages",
        headdmNoMsgs: "No messages yet.",
        headdmCloseAria: "Close chat",

        chatrowSayHi: "Say hi",
        chatrowSayHiLabel: "Say hi",
      },

      fr: {
        textNoMore: "— Plus de conversations —",
        textHE: "Ton ami",
        texttyoing: "est en train d’écrire…",
        loginHint: "Connectez-vous, trouvez des amis et commencez à discuter",
        loginBubbleAria: "Connectez-vous pour utiliser les messages",
        ariaOpenMessages: "Ouvrir les messages",
        ariaCloseMessages: "Fermer les messages",

        chatsTitle: "TecheChat",
        seeAllTitle: "Tout voir",
        searchPlaceholder: "Rechercher dans les discussions",
        searchingLabel: "Recherche…",
        noResults: "Aucun résultat",

        panelLoginMessage: "Connectez-vous pour utiliser les messages.",
        loadMore: "Charger plus",
        noChats: "Aucune discussion pour l’instant",
        findFriendsCta: "Trouver des amis dans la communauté",
        seeAllFooter: "Tout voir dans Messenger",

        headdmPlaceholder: "Écrire un message…",
        headdmLoadPrev: "Charger les messages précédents",
        headdmNoMsgs: "Aucun message pour l’instant.",
        headdmCloseAria: "Fermer la discussion",

        chatrowSayHi: "Dites bonjour",
        chatrowSayHiLabel: "Dites bonjour",
      },
    }),
    []
  );

  const [t, setT] = useState(baseTextByLang.it);

  useEffect(() => {
    // For Italian / English / French → use static text
    if (lang === "it" || lang === "en" || lang === "fr") {
      setT(baseTextByLang[lang] || baseTextByLang.it);
      return;
    }

    // For other languages → translate from Italian base
    let cancelled = false;
    (async () => {
      try {
        const source = baseTextByLang.it;
        const entries = Object.entries(source);

        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            if (!value) return [key, value];
            const out = await translateText(value);
            return [key, out || value]; // fallback to Italian
          })
        );

        if (!cancelled) {
          setT(Object.fromEntries(translatedEntries));
        }
      } catch {
        if (!cancelled) {
          setT(baseTextByLang.it);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lang, translateText, baseTextByLang]);

  const navigate = useNavigate();

  // root ref to detect outside clicks
  const rootRef = useRef(null);

  // FAB toggle
  const [open, setOpen] = useState(false);

  // auth/user
  const [me, setMe] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  });



  // ---- WEBSOCKET SHARED REF ----
  const wsRef = useRef(null);

  useEffect(() => {
    if (!me?.email) return;

    // Close old socket if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    const url = `${WS_URL}?email=${encodeURIComponent(me.email)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("[WS] connected");
    };

    ws.onerror = (e) => {
      console.error("[WS] error:", e);
    };

    ws.onclose = (e) => {
      console.log("[WS] closed:", e.code, e.reason);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle WS incoming messages
        if (data.type === "typing") {
          // TODO: handled inside HeadDM (we do nothing here)
        }

        if (data.type === "pong") {
          // later for debugging
        }
      } catch (err) {
        console.log("[WS] raw:", event.data);
      }
    };

    return () => {
      ws.close();
    };
  }, [me?.email]);


  
  useEffect(() => {
    const readUser = () => {
      try {
        return JSON.parse(localStorage.getItem("user"));
      } catch {
        return null;
      }
    };

    const handleLogin = (e) => {
      const u = e?.detail || readUser();
      setMe(u || null);
    };

    const handleLogout = () => {
      setMe(null);
    };

    window.addEventListener("auth:login", handleLogin);
    window.addEventListener("auth:logout", handleLogout);

    return () => {
      window.removeEventListener("auth:login", handleLogin);
      window.removeEventListener("auth:logout", handleLogout);
    };
  }, []);



  // presence ping for live status
  useEffect(() => {
    if (!me?.email) return;

    const ping = () => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;

      ws.send(JSON.stringify({
        op: "ping",
        email: me.email
      }));
    };

    // send one immediately when the socket is ready
    ping();

    const id = setInterval(() => {
      if (document.visibilityState === "visible") {
        ping();
      }
    }, 15000);

    return () => clearInterval(id);
  }, [me?.email]);




  // conversations list
  const [loadingConvos, setLoadingConvos] = useState(false);
  const [convos, setConvos] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [activeLastReadAt, setActiveLastReadAt] = useState(0);

  // active thread in the panel
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [msgBefore, setMsgBefore] = useState(null);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  // search
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]); // [{id, name, preview, createdAt, unread}]

  // chat-heads state (each head: {id, name, avatar, open})
  const [heads, setHeads] = useState([]);

  // unread count (like header: number of conversations with unread > 0)
  const [unreadCount, setUnreadCount] = useState(0);

  // Shared size for all circular bubbles (main button + avatar heads)
  const bubbleSizeClass = "w-12 h-12";

  // date locale for formatting inside chat
  const dateLocale = useMemo(() => {
    // Map your main UI langs to browser locales
    if (lang === "it") return "it-IT";
    if (lang === "fr") return "fr-FR";
    if (lang === "en") return "en-US";

    // For other languages, try the code itself (ar, es, de, etc.)
    return lang || undefined;
  }, [lang]);


  // derive unreadCount from convos whenever they change
  useEffect(() => {
    const unread = (convos || []).filter(c => (c.unread || 0) > 0).length;
    setUnreadCount(unread);
  }, [convos]);

  // To open/toggle a head
  function openHead({ id, name, avatar }) {
    if (!id) return;
    setHeads(prev => {
      const hit = prev.find(h => h.id === id);
      if (hit) {
        return prev.map(h =>
          h.id === id
            ? {
                ...h,
                open: true,
                name: name || h.name,
                avatar: avatar || h.avatar
              }
            : h
        );
      }
      return [
        ...prev,
        {
          id,
          name: name || "Chat",
          avatar: avatar || "/avatar.png",
          open: true
        }
      ];
    });
  }
  function closeHead(id) {
    setHeads(prev => prev.filter(h => h.id !== id));
  }
  function toggleHead(id) {
    setHeads(prev =>
      prev.map(h => (h.id === id ? { ...h, open: !h.open } : h))
    );
  }

  // DMPage can tell ChatFloat to close + clear bubbles
  useEffect(() => {
    const closeAll = () => {
      setOpen(false);
      setHeads([]);
    };
    window.addEventListener("chatfloat:close", closeAll);
    return () => window.removeEventListener("chatfloat:close", closeAll);
  }, []);


  // ESC closes everything
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        // close main panel
        setOpen(false);

        // close all chat-head bubbles
        setHeads([]);

        // close active thread inside the panel if needed
        setActive(null);
      }
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);


  // fetch conversations when panel opens
  useEffect(() => {
    if (!open || !me?.email) return;
    let cancelled = false;
    (async () => {
      setLoadingConvos(true);
      const url = new URL(`${BASE}/conversations_list`);
      url.searchParams.set("email", me.email);
      const res = await fetch(url);
      const data = await res.json();
      if (!cancelled) {
        setConvos(data.items || []);
        setCursor(data.nextCursor || null);
      }
      setLoadingConvos(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, me?.email]);

  // external "open chat panel" event
  useEffect(() => {
    const onOpen = e => {
      const wanted = e?.detail?.convId;
      setOpen(true);

      // If a specific conversation is requested, set it + load messages
      if (wanted) {
        setActive({ id: wanted });
        setActiveLastReadAt(
          (convos.find(c => c.id === wanted)?.lastReadAt) || 0
        );
        setMsgs([]); // reset thread list first
        setMsgBefore(null); // reset paging cursor
        loadMessages(wanted); // fetch
      } else {
        // if no specific id, just refresh the list
        if (me?.email) {
          const url = new URL(`${BASE}/conversations_list`);
          url.searchParams.set("email", me.email);
          fetch(url)
            .then(r => r.json())
            .then(d => setConvos(d.items || []));
        }
      }
    };

    window.addEventListener("chat:open", onOpen);
    return () => window.removeEventListener("chat:open", onOpen);
  }, [me?.email, convos]);

  // friends:accepted → open panel, refresh list, optionally open that conversation
  useEffect(() => {
    const h = e => {
      setOpen(true);
      if (me?.email) {
        const url = new URL(`${BASE}/conversations_list`);
        url.searchParams.set("email", me.email);
        fetch(url)
          .then(r => r.json())
          .then(d => setConvos(d.items || []));
      }
      const id = e?.detail?.convId;
      if (id) {
        setActive({ id });
        setActiveLastReadAt(
          (convos.find(c => c.id === id)?.lastReadAt) || 0
        );
        setMsgs([]);
        setMsgBefore(null);
        loadMessages(id);
      }
    };
    window.addEventListener("friends:accepted", h);
    return () => window.removeEventListener("friends:accepted", h);
  }, [me?.email, convos]);

  const loadMoreConvos = async () => {
    if (!cursor || !me?.email) return;
    const url = new URL(`${BASE}/conversations_list`);
    url.searchParams.set("email", me.email);
    url.searchParams.set("cursor", cursor);
    const res = await fetch(url);
    const data = await res.json();
    setConvos(prev => [...prev, ...(data.items || [])]);
    setCursor(data.nextCursor || null);
  };


  // refresh conversations list in background
  const refreshConvos = async () => {
    if (!me?.email) return;

    setLoadingConvos(true);
    const url = new URL(`${BASE}/conversations_list`);
    url.searchParams.set("email", me.email);
    const res = await fetch(url);
    const data = await res.json();
    setConvos(data.items || []);
    setCursor(data.nextCursor || null);
    setLoadingConvos(false);
  };



  const loadMessages = async (convId, before = null) => {
    const url = new URL(`${BASE}/messages_page`);
    url.searchParams.set("convId", convId);
    if (before) url.searchParams.set("before", before);
    const res = await fetch(url);
    const data = await res.json();
    const reversed = data.items.reverse(); // oldest → newest
    setMsgs(prev => (before ? [...reversed, ...prev] : reversed));
    setMsgBefore(
      data.items.length
        ? data.items[data.items.length - 1].createdAt
        : null
    );
    if (!before && me?.email) {
      fetch(
        `${BASE}/messages_read?convId=${encodeURIComponent(
          convId
        )}&email=${encodeURIComponent(
          me.email
        )}&upToCreatedAt=${Date.now()}`
      ).catch(() => {});
      setActiveLastReadAt(Date.now());
    }
  };

  // open first convo automatically once we have list and panel is open
  useEffect(() => {
    if (!open) return;
    if (active || !convos.length) return;
    setActive(convos[0]);
    setActiveLastReadAt(convos[0].lastReadAt || 0);
    loadMessages(convos[0].id);
  }, [open, convos, active]);

  /* autoscroll inside panel
  useEffect(() => {
    if (!open) return;
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth"
    });
  }, [open, msgs.length]);
*/
  const send = async () => {
    const t = text.trim();
    if (!t || !active || !me?.email) return;
    setSending(true);
    setText("");

    const dedupeKey = `${deviceId}:${Date.now()}`;
    const optimistic = {
      id: dedupeKey,
      convId: active.id,
      senderEmail: me.email,
      text: t,
      createdAt: Date.now()
    };
    setMsgs(prev => [...prev, optimistic]);

    const qs = new URLSearchParams({
      convId: active.id,
      senderEmail: me.email,
      text: t,
      dedupeKey
    });
    try {
      const res = await fetch(`${BASE}/messages_send`, {
        method: "POST",
        body: qs,
        headers: { "Content-Type": "application/x-www-form-urlencoded" }
      });
      const data = await res.json();
      setMsgs(prev =>
        prev.map(m => (m.id === dedupeKey ? data.msg : m))
      );
    } catch {
      setMsgs(prev =>
        prev.map(m =>
          m.id === dedupeKey ? { ...m, failed: true } : m
        )
      );
    } finally {
      setSending(false);
    }
  };

  // lightweight “realtime” for the active conversation in the panel
  useEffect(() => {
    if (!open) return;
    let aborted = false;
    let since = Date.now();
    const tick = async () => {
      if (!active) {
        if (!aborted) setTimeout(tick, 500);
        return;
      }
      try {
        const res = await fetch(
          `${BASE}/live_events?convId=${active.id}&since=${since}`,
          {
            signal: AbortSignal.timeout
              ? AbortSignal.timeout(25000)
              : undefined
          }
        );
        if (!res.ok) throw new Error();
        const ev = await res.json(); // {newMessages:[...], now}
        since = ev.now || Date.now();
        if (ev.newMessages?.length) {
          setMsgs(prev => [...prev, ...ev.newMessages]);
        }
      } catch {
        // ignore errors, short sleep
      }
      if (!aborted) setTimeout(tick, 500);
    };
    tick();
    return () => {
      aborted = true;
    };
  }, [open, active?.id]);

  // Debounced search across ALL conversations
  useEffect(() => {
    if (!open || !me?.email) return;
    const tId = setTimeout(async () => {
      if (!q.trim()) {
        setResults([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      const url = new URL(`${BASE}/conversations_search`);
      url.searchParams.set("email", me.email);
      url.searchParams.set("q", q.trim());
      url.searchParams.set("limit", "20");
      const res = await fetch(url);
      const data = await res.json();
      setResults(data.items || []);
      setSearching(false);
    }, 300);
    return () => clearTimeout(tId);
  }, [open, q, me?.email]);

  
  // tiny DM window used by heads
  function HeadDM({ convId, name, avatar, onClose, onMinimize, wsRef }) {
    const me = useMemo(() => {
      try {
        return JSON.parse(localStorage.getItem("user"));
      } catch {
        return null;
      }
    }, []);
    const [msgs, setMsgs] = useState([]);
    const [before, setBefore] = useState(null);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [typingUsers, setTypingUsers] = useState([]);
    const [isOnline, setIsOnline] = useState(false);
    const lastOnlineRef = useRef(0);
    const scrollRef = useRef(null);
    const observers = useRef({});


    const peerEmail = useMemo(() => {
      if (!me?.email) return null;
      const myEmail = me.email.toLowerCase();

      // find the first message NOT from me → that’s the other side
      const otherMsg = msgs.find(
        (m) => m.senderEmail && m.senderEmail.toLowerCase() !== myEmail
      );

      return otherMsg ? otherMsg.senderEmail.toLowerCase() : null;
    }, [msgs, me?.email]);

    

    // Mark messages as seen
    function markRead(convId, createdAt) {
      fetch(
        `${BASE}/messages_read?convId=${convId}&email=${me.email}&upToCreatedAt=${createdAt}`
      ).catch(() => {});
    }

    async function load(olderThan = null) {
      const url = new URL(`${BASE}/messages_page`);
      url.searchParams.set("convId", convId);
      if (olderThan) url.searchParams.set("before", olderThan);
      const r = await fetch(url);
      const d = await r.json();
      const arr = d.items.reverse();
      setMsgs(prev => (olderThan ? [...arr, ...prev] : arr));
      setBefore(arr.length ? arr[0].createdAt : null);
      if (!olderThan && me?.email) {
        fetch(
          `${BASE}/messages_read?convId=${encodeURIComponent(
            convId
          )}&email=${encodeURIComponent(
            me.email
          )}&upToCreatedAt=${Date.now()}`

        ).catch(() => {});
      }
    }




    useEffect(() => {
      load();
    }, [convId]);

    useEffect(() => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth"
      });
    }, [msgs.length]);


    // observe messages to mark them as read when visible
    useEffect(() => {
      msgs.forEach(m => {
        if (m.senderEmail === me?.email) return;
        if (m.read) return;

        if (!observers.current[m.id]) {
          const el = document.getElementById("msg-" + m.id);
          if (!el) return;

          observers.current[m.id] = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                markRead(convId, m.createdAt);
              }
            });
          });

          observers.current[m.id].observe(el);
        }
      });
    }, [msgs]);


    useEffect(() => {
      let stop = false,
        since = Date.now();
      const tick = async () => {
        try {
          const r = await fetch(
            `${BASE}/live_events?convId=${convId}&since=${since}`,
            {
              signal: AbortSignal.timeout
                ? AbortSignal.timeout(25000)
                : undefined
            }
          );
          if (!r.ok) throw new Error();
          const ev = await r.json();
          since = ev.now || Date.now();
          if (ev.newMessages?.length) {
            setMsgs(p => [...p, ...ev.newMessages]);
          }
          if (ev.typing) {
            const now = Date.now();
            const activeTyping = Object.entries(ev.typing)
              .filter(([email, ts]) => email !== me.email && now - ts < 4000)
              .map(([email]) => email);

            setTypingUsers(activeTyping);
          }
        } catch {
          // ignore
        }
        if (!stop) setTimeout(tick, 500);
      };
      tick();
      return () => {
        stop = true;
      };
    }, [convId]);


    // POLL PRESENCE
    useEffect(() => {
      if (!peerEmail) return; // no peer yet (no messages), skip

      const interval = setInterval(async () => {
        try {
          const r = await fetch(
            `${BASE}/presence?email=${encodeURIComponent(peerEmail)}`
          );
          const d = await r.json();
          const now = Date.now();
          if (d.online) {
            // remember last time we saw them online
            lastOnlineRef.current = now;
            setIsOnline(true);
          } else {
            // only flip to offline if they’ve been offline for >60s
            if (now - lastOnlineRef.current > 60000) {
              setIsOnline(false);
            }
          }
        } catch (e) {
          // ignore errors
        }
      }, 1000);

      return () => clearInterval(interval);
    }, [peerEmail]);


    async function send() {
      const t = text.trim();
      if (!t || !me?.email) return;
      setSending(true);
      setText("");
      const dk = `${deviceId}:${Date.now()}`;
      const optimistic = {
        id: dk,
        convId,
        senderEmail: me.email,
        text: t,
        createdAt: Date.now()
      };
      setMsgs(p => [...p, optimistic]);
      try {
        const body = new URLSearchParams({
          convId,
          senderEmail: me.email,
          text: t,
          dedupeKey: dk
        });
        const r = await fetch(`${BASE}/messages_send`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body
        });
        const d = await r.json();
        setMsgs(p => p.map(m => (m.id === dk ? d.msg : m)));
      } catch {
        setMsgs(p =>
          p.map(m =>
            m.id === dk ? { ...m, failed: true } : m
          )
        );
      } finally {
        setSending(false);
      }
    }

    const isSameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    const formatDayLabel = (d) =>
      d.toLocaleDateString(dateLocale || undefined, {
        weekday: "short",   // Tue
        month: "short",     // Nov
        day: "numeric",     // 26
      });

    const formatTimeLabel = (d) =>
      d.toLocaleTimeString(dateLocale || undefined, {
        hour: "2-digit",
        minute: "2-digit",
      });


    return (
<div
  className="
    bg-white rounded-2xl shadow-2xl border overflow-hidden flex flex-col
    w-[min(310px,90vw)]
    sm:w-[min(330px,80vw)]
    lg:w-[min(350px,26vw)]

    /* height responsiveness */
    min-h-[220px]
    sm:min-h-[240px]
    md:min-h-[260px]

    max-h-[35vh]       /* very small phones – never more than ~55% of screen */
    sm:max-h-[50vh]    /* normal phones */
    md:max-h-[45vh]    /* small tablets / small laptops */
    lg:max-h-[45vh]    /* desktops: about half screen height */
    xl:max-h-[420px]   /* big desktops: cap at 420px for a compact look */
  "
>

        <div className="flex items-center justify-between px-2.5 py-2 sm:px-3 border-b shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className="relative">
              <img
                src={avatar || "/avatar.png"}
                className="w-7 h-7 rounded-full object-cover"
                alt=""
              />
              {isOnline && (
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border border-white"></span>
              )}
            </div>

            <div className="truncate font-semibold text-xs sm:text-sm">
              {name || "Chat"}
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* Minimize button */}
            <button
              onClick={onMinimize}
              className="p-1 rounded hover:bg-gray-100"
              aria-label="Minimize chat"
            >
              <span className="inline-flex items-center justify-center text-xs font-bold w-4">—</span>
              
            </button>

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100"
              aria-label={t.headdmCloseAria}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* MESSAGES LIST (flex-1, scrolls) */}
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 px-2.5 py-2 sm:px-3 overflow-y-auto"
        >
          {before && msgs.length > 0 && (
            <button
              onClick={() => load(before)}
              className="block mx-auto mb-2 text-[10px] sm:text-xs text-[#324B8E] hover:nounderline"
            >
              {t.headdmLoadPrev}
            </button>
          )}
          {msgs.map((m, idx) => {
            const mine =
              m.senderEmail?.toLowerCase() ===
              me?.email?.toLowerCase();

            const currentDate = new Date(+m.createdAt);
            const prevMsg = idx > 0 ? msgs[idx - 1] : null;
            const prevDate = prevMsg ? new Date(+prevMsg.createdAt) : null;

            // show date header if first message OR day changed
            const showDayHeader = !prevDate || !isSameDay(currentDate, prevDate);

            const dayLabel = formatDayLabel(currentDate);   // e.g. "Tue, Nov 26"
            const timeLabel = formatTimeLabel(currentDate); // e.g. "5:45 PM"

            return (
              <React.Fragment key={m.id}>
                {showDayHeader && (
                  <div className="text-center text-[10px] sm:text-[11px] text-gray-500 my-2">
                    {dayLabel}
                  </div>
                )}

                <div
                  className={
                    "mb-1.5 flex " +
                    (mine ? "justify-end" : "justify-start")
                  }
                >
                  <div
                    id={`msg-${m.id}`}
                    className={
                      "max-w-[80%] rounded-2xl px-3 py-1.5 sm:py-2 text-xs sm:text-sm leading-snug " +
                      (mine
                        ? "bg-[#324B8E] text-white"
                        : "bg-gray-100 text-gray-900")
                    }
                  >
                    <div className="whitespace-pre-wrap break-words">
                      {m.text}
                    </div>
                    <div className="text-[9px] sm:text-[10px] opacity-70 mt-1 text-right">
                      {timeLabel}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {!msgs.length && (
            <div className="text-center text-xs text-gray-500 py-6">
              {t.headdmNoMsgs}
            </div>
          )}
          {typingUsers.length > 0 && (
            <div className="px-3 py-1 text-xs text-gray-500 opacity-80">
              {(name && name.trim()) || t.textHE} {t.texttyoing}
            </div>
          )}
        </div>

        {/* INPUT ROW */}
        <div className="border-t px-2.5 py-2 sm:px-3 flex items-center gap-2 shrink-0">
          <input
            value={text}
            onChange={e => {
              setText(e.target.value);

              if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                  op: "typing",
                  convId,
                  email: me.email,
                  to: peerEmail
                }));
              }
            }}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            className="flex-1 border rounded-xl px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm outline-none focus:ring-2 focus:ring-blue-300"
            placeholder={t.headdmPlaceholder}
          />
          <button
            onClick={send}
            disabled={sending || !text.trim()}
            className="p-1.5 sm:p-2 rounded-xl bg-[#324B8E] text-white disabled:opacity-50"
          >
            {sending ? (
              <Loader2 className="animate-spin w-3.5 h-3.5 sm:w-4 sm:h-4" />
            ) : (
              <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            )}
          </button>
        </div>
      </div>
    );
  }

  // external trigger to open a head (from DMPage / PeopleMap / etc.)
  useEffect(() => {
    const h = e => {
      const { convId, name, avatar } = e.detail || {};

      // If we are already on /messages or /dm, don't spawn a floating head
      if (isMessageRoute) return;

      if (convId) {
        setOpen(false); // close main panel
        openHead({ id: convId, name, avatar });
      }
    };
    window.addEventListener("dm:open", h);
    return () => window.removeEventListener("dm:open", h);
  }, [isMessageRoute]);

  // helpers
  const ChatRow = ({ item }) => {
    const preview = (item.preview ?? item.lastMessage ?? "").trim();
    const isEmpty = preview.length === 0;
    const hasUnread = (item.unread || 0) > 0;

    return (
      <button
        onClick={() => {
          setOpen(false);
          openHead({
            id: item.id,
            name: item.name,
            avatar: item.avatar || item.image
          });
        }}
        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 border-b text-left"
      >
        <img
          src={item.avatar || item.image || "/avatar.png"}
          alt=""
          className="w-10 h-10 rounded-full object-cover"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div
              className={
                "text-sm truncate " +
                (hasUnread
                  ? "font-semibold text-gray-900"
                  : "font-medium")
              }
            >
              {item.name || "User"}
            </div>
            {hasUnread && (
              <span className="ml-auto w-2 h-2 rounded-full bg-blue-600" />
            )}
          </div>
          {isEmpty ? (
            <div
              className={
                "text-xs flex items-center gap-1 " +
                (hasUnread
                  ? "text-gray-900 font-medium"
                  : "text-gray-500")
              }
            >
              <Hand className="w-3.5 h-3.5" />
              <span>{t.chatrowSayHi}</span>
            </div>
          ) : (
            <div
              className={
                "text-xs truncate " +
                (hasUnread
                  ? "text-gray-900 font-medium"
                  : "text-gray-600")
              }
            >
              {preview}
            </div>
          )}
        </div>
      </button>
    );
  };

  // click outside root closes panel + heads
  useEffect(() => {
    function onDocPointerDown(e) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setOpen(false); // close panel
        //setHeads([]); // remove all avatar bubbles
      }
    }
    document.addEventListener("pointerdown", onDocPointerDown);
    return () =>
      document.removeEventListener("pointerdown", onDocPointerDown);
  }, []);

  // hide everything on messages routes
  //if (isMessageRoute) return null;

  // FACEBOOK-style login bubble when user is not logged in
  if (!me?.email) {
    return (
      <div className="fixed bottom-4 right-4 z-[9998] flex flex-col items-end gap-2">
        {/* Tooltip pill (like Messenger hint) */}
        <div className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white shadow-md border text-xs text-gray-800">
          <span>{t.loginHint}</span>
        </div>

        {/* Round Messenger bubble */}
        <button
          aria-label={t.loginBubbleAria}
          onClick={() => navigate("/login")}
          className={
            `rounded-full shadow-lg bg-[#324B8E] text-white 
             focus:outline-none focus:ring-4 focus:ring-blue-300 
             flex items-center justify-center relative ` + bubbleSizeClass
          }
        >
          {/* Icon + subtle pulse like Facebook */}
          <span className="absolute inset-0 rounded-full animate-ping bg-blue-500/30 pointer-events-none" />
          <div className="relative flex items-center justify-center">
            <MessageCircle className="w-6 h-6" />
          </div>
        </button>
      </div>
    );
  }

  return (
    <div ref={rootRef}>
      {/* Floating circle with unread counter */}
      {showFloatUI && (
        <button
          aria-label={open ? t.ariaCloseMessages : t.ariaOpenMessages}
          onClick={() => { setOpen(v => !v); setHeads([]); }}
          className={
            `fixed bottom-4 right-4 z-[9998] rounded-full shadow-lg bg-[#324B8E] text-white 
            focus:outline-none focus:ring-4 focus:ring-blue-300 flex items-center justify-center ` + bubbleSizeClass
          }
        >
        <div className="relative">
          {open ? (
            <X className="w-6 h-6" />
          ) : (
            <MessageCircle className="w-6 h-6" />
          )}

          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 text-[10px] text-white items-center justify-center">
                {unreadCount}
              </span>
            </span>
          )}
        </div>
      </button>
    )}

      {/* bubbles to the LEFT of the ChatFloat button */}
      {showFloatUI && (
        <div
          className="fixed bottom-4 z-[9998] flex items-end gap-3"
          style={{
            right: "calc(env(safe-area-inset-right, 0px) + 88px)"
          }}
        >
          {heads.map(h => (
            <button
              key={h.id}
              onClick={() => toggleHead(h.id)}
              className={`${bubbleSizeClass} rounded-full border bg-white shadow-lg overflow-hidden ring-2 ring-white`}
              title={h.name}
            >
              <img
                src={h.avatar || "/avatar.png"}
                alt=""
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* anchored mini DM windows (above the bubbles, stack leftwards from the right edge) */}
      {showFloatUI && (
        <div
          className="fixed z-[9999] flex flex-row-reverse items-end gap-3"
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
            right: "calc(env(safe-area-inset-right, 0px) + 16px)"
          }}
        >
          {heads.map(
            h =>
              h.open && (
                <HeadDM
                  key={h.id}
                  convId={h.id}
                  name={h.name}
                  avatar={h.avatar}
                  wsRef={wsRef}
                  onClose={() => closeHead(h.id)}
                  onMinimize={() => toggleHead(h.id)}
                />
              )
          )}
        </div>
      )}

      {/* Panel */}
      {showFloatUI && open && (
        <div
          className="fixed right-4 bottom-24 z-[9999]
                      w-[min(230px,85vw)]
                      sm:w-[min(250px,72vw)]
                      md:w-[min(260px,55vw)]
                      lg:w-[min(280px,26vw)]
                      xl:w-[min(300px,20vw)]
                      2xl:w-[min(320px,16vw)]
                      max-h-[100vh] bg-white rounded-2xl shadow-xl border flex flex-col"
        >
          {/* Header */}
          <div className="p-3 border-b">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">{t.chatsTitle}</h3>
              <button
                aria-label={t.seeAllTitle}
                onClick={() => {
                  setOpen(false);
                  navigate("/messages");
                }}
                className="p-1 rounded hover:bg-gray-100"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-500" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="w-full pl-8 pr-2 py-2 text-sm border rounded-xl outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
          </div>

          {/* BODY */}
          <div
            className="flex flex-1 min-h-0 flex-col overflow-y-auto scroll-smooth"
            onTouchStart={(e) => {
              const el = e.currentTarget;
              el._pullStartY = e.touches[0].clientY;
              el._pulling = false;
            }}
            onTouchMove={(e) => {
              const el = e.currentTarget;
              const diff = e.touches[0].clientY - el._pullStartY;

              if (el.scrollTop === 0 && diff > 60 && !el._pulling) {
                el._pulling = true;
                refreshConvos();
              }
            }}
          >
            {/* Search Results */}
            {q.trim() ? (
              <>
                {searching && (
                  <div className="p-3 text-sm text-gray-500">{t.searchingLabel}</div>
                )}

                {!searching &&
                  results.map(r => <ChatRow key={r.id} item={r} />)}

                {!searching && !results.length && q.trim() && (
                  <div className="p-3 text-sm text-gray-500">{t.noResults}</div>
                )}
              </>
            ) : (
              <>
                {/* Conversation List */}
                {convos.map(c => (
                  <ChatRow key={c.id} item={c} />
                ))}

                {/* Skeleton Loader for infinite scroll */}
                {loadingConvos && (
                  <div className="px-3 py-2 space-y-3 animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full" />
                      <div className="flex-1">
                        <div className="h-3 bg-gray-200 rounded w-1/2 mb-1"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full" />
                      <div className="flex-1">
                        <div className="h-3 bg-gray-200 rounded w-2/3 mb-1"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* No more conversations */}
                {!cursor && convos.length > 0 && !loadingConvos && (
                  <div className="p-3 text-[12px] text-gray-500 text-center opacity-70">
                    {t.textNoMore}
                  </div>
                )}

                {/* No conversations at all */}
                {!loadingConvos && !convos.length && (
                  <div className="p-3 text-sm text-gray-500 flex flex-col items-center gap-2">
                    <div>{t.noChats}</div>
                    <button
                      onClick={() => {
                        setOpen(false);
                        navigate("/community/2");
                      }}
                      className="mt-1 px-3 py-1.5 rounded-full bg-[#324B8E] text-white text-xs shadow-sm hover:bg-[#283a6b] transition"
                    >
                      {t.findFriendsCta}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Footer "See all" */}
            <button
              onClick={() => {
                setOpen(false);
                navigate("/messages");
              }}
              className="w-full py-2 text-sm text-[#324B8E] hover:underline border-t"
            >
              {t.seeAllFooter}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
